import { SelectInput } from "@impero-com/common/components/form/fields/selectInput";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { FormField } from "@impero-com/ui";
import { Checkbox } from "antd";
import { cx } from "class-variance-authority";
import { Control, useController } from "react-hook-form";

import {
    DayOfWeek,
    ControlRecurrenceRule,
    WeeklyRecurrenceRule,
} from "api/generated/model";
import { extractNestedError } from "utils/form/extractNestedError";

import { translateDayOfWeek } from "../../list/utils";
import { ControlViewFormSchema } from "../formSchema";

import * as theme from "./styles.css";
import { TimeAndTimezone } from "./TimeAndTimezone";

const WEEK_DAYS: DayOfWeek[] = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];
const EVERY_N_WEEKS = [1, 2, 3, 4, 5, 6];

type ControlRecurrenceRuleWeekly = WeeklyRecurrenceRule & { type: "Weekly" };

export interface WeeklyRecurrenceProps {
    control: Control<ControlViewFormSchema>;
    disabled?: boolean;
}

export const WeeklyRecurrence = ({
    control,
    disabled,
}: WeeklyRecurrenceProps) => {
    const { t } = useTranslation();
    const { field: ruleField, fieldState } = useController({
        control,
        name: "scheduling.rule",
        disabled,
    });
    const value = ruleField.value as ControlRecurrenceRuleWeekly;

    const daysError = extractNestedError(fieldState.error);

    // eslint-disable-next-line react-hooks/refs
    const noDaysSelected = value.days.length === 0;
    // eslint-disable-next-line react-hooks/refs
    const allDaysSelected = value.days.length === WEEK_DAYS.length;
    const allDaysIndeterminate = !noDaysSelected && !allDaysSelected;

    const handleAllDaysChange = (checked: boolean) => {
        ruleField.onChange({ ...value, days: checked ? [...WEEK_DAYS] : [] });
    };

    const handleDaysChange = (checkedDays: DayOfWeek[]) => {
        ruleField.onChange({ ...value, days: checkedDays });
    };

    const handlePeriodicityChange = (
        periodicity: string | number | null | undefined
    ) => {
        ruleField.onChange({ ...value, periodicity: periodicity as number });
    };

    const weekDayOptions = WEEK_DAYS.map((day) => ({
        label: translateDayOfWeek(day),
        value: day,
    }));
    const periodicityOptions = EVERY_N_WEEKS.map((count) => ({
        label: t("CreateControl_Recurrence_Weekly_Periodicity", { count }),
        value: count,
    }));

    return (
        <>
            {/* eslint-disable-next-line react-hooks/refs */}
            <div ref={ruleField.ref} tabIndex={-1}>
                <FormField
                    error={daysError}
                    className={theme.marginBottom}
                    lite
                >
                    <div>
                        <Checkbox
                            checked={allDaysSelected}
                            indeterminate={allDaysIndeterminate}
                            onChange={(e) =>
                                handleAllDaysChange(e.target.checked)
                            }
                            disabled={disabled}
                        >
                            {t("CreateControl_Recurrence_Weekly_AllDays")}
                        </Checkbox>
                        <Checkbox.Group
                            // eslint-disable-next-line react-hooks/refs
                            value={value.days}
                            onChange={(checked) =>
                                handleDaysChange(checked as DayOfWeek[])
                            }
                            options={weekDayOptions}
                            disabled={disabled}
                        />
                    </div>
                </FormField>
            </div>
            <div className={cx(theme.inputRow, theme.cancelMarginBottom)}>
                <SelectInput
                    // eslint-disable-next-line react-hooks/refs
                    value={value.periodicity}
                    onChange={handlePeriodicityChange}
                    onBlur={() => {}}
                    options={periodicityOptions}
                    label={t("CreateControl_Recurrence_Weekly_Every")}
                    disabled={disabled}
                    nodeAfter={
                        <>
                            {t("CreateControl_Recurrence_At")}
                            <TimeAndTimezone
                                control={control}
                                disabled={disabled}
                            />
                        </>
                    }
                />
            </div>
        </>
    );
};

export function isWeeklyRecurrenceRule(
    rule: ControlRecurrenceRule
): rule is ControlRecurrenceRuleWeekly {
    return rule.type === "Weekly";
}
